/* eslint-disable max-lines */
import { IonCol, IonGrid, IonRow, IonText } from '@ionic/react';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router';

import ActivityStage from '@components/ActivityStage';
import BackButton from '@components/buttons/BackButton';
import PrimaryButton from '@components/buttons/PrimaryButton';
import SecondaryButton from '@components/buttons/SecondaryButton';
import TertiaryButton from '@components/buttons/TertiaryButton';
import DateStamp from '@components/dates/DateStamp';
import DeleteConfirmationAlert from '@components/DeleteConfirmationAlert';
import InternalNoteForm from '@components/forms/InternalNoteForm';
import InitialConsultMenu from '@components/InitialConsultMenu';
import InitialConsultQuestion from '@components/InitialConsultQuestion';
import styles from '@components/InitialConsultReport.module.css';
import InternalNote from '@components/InternalNote';
import Modal from '@components/Modal';
import Nbsp from '@components/Nbsp';
import NeedMet from '@components/NeedMet';
import StageInfo from '@components/StageInfo';
import DateGroupBanner from '@components/text/DateGroupBanner';
import HugeText from '@components/text/HugeText';
import LargeText from '@components/text/LargeText';
import useDestroyInternalNote from '@hooks/mutations/useDestroyInternalNote';
import useGlobalOpener from '@hooks/useGlobalOpener';
import useMutationWithValidation from '@hooks/useMutationWithValidation';
import useMyExpert from '@hooks/useMyExpert';
import useOpener from '@hooks/useOpener';
import useSanitizeHtml from '@hooks/useSanitizeHtml';
import {
  CareProfileCaregiverTypeEnum,
  CareProfileEmotionalConcernEnum,
  CareProfileHealthInsuranceTypeEnum,
  CareProfileInsuranceTypeEnum,
  CareProfileMedicalConditionEnum,
  CareProfileReligionEnum,
  CareProfileResidenceTypeEnum,
  ClientRoleRelationshipEnum,
  FocusAreaEnum,
  GetJourneyWithInitialConsultQuery,
  InitialConsultReportPublishDocument,
  InitialConsultReportPublishMutation,
  InitialConsultReportPublishMutationVariables,
  InternalNoteFragment as InternalNoteType
} from '@typing/Generated';
import { InitialConsultReportSection } from '@typing/Models';
import { OpenerType } from '@typing/State';
import { countryNameForCode } from '@utils/countryUtils';
import { languageNameForCode } from '@utils/languageUtils';
import { groupByTimestampDay, initialConsultCurrentSection } from '@utils/modelUtils';
import { journeyRoute } from '@utils/routeUtils';

type Props = {
  journey: GetJourneyWithInitialConsultQuery['journey'];
};

const InitialConsultReport = ({ journey }: Props) => {
  const intl = useIntl();
  const myExpert = useMyExpert();
  const location = useLocation();
  const currentSection = useMemo(() => initialConsultCurrentSection(location.pathname), [location]);

  const internalNoteDeleteOpener = useOpener();
  const [internalNoteBeingDeleted, setInternalNoteBeingDeleted] = useState<InternalNoteType | undefined>(undefined);
  const timeTrackingOpener = useGlobalOpener(OpenerType.TIME_LOG);

  const internalNoteModalOpener = useOpener();
  const [internalNoteBeingEdited, setInternalNoteBeingEdited] = useState<InternalNoteType | undefined>(undefined);

  const [initialConsultReportPublish] = useMutationWithValidation<
    InitialConsultReportPublishMutation,
    InitialConsultReportPublishMutationVariables
  >(InitialConsultReportPublishDocument, 'initialConsultReport');

  const handlePublish = useCallback(() => {
    initialConsultReportPublish({ id: journey.initialConsultReport.id }).then(() => {
      timeTrackingOpener.open();
    });
  }, [initialConsultReportPublish, journey.initialConsultReport.id, timeTrackingOpener]);

  const destroyInternalNote = useDestroyInternalNote({ journeyId: journey.id });

  const handleInternalNoteDelete = useCallback(() => {
    if (internalNoteBeingDeleted) {
      destroyInternalNote({ id: internalNoteBeingDeleted.id });
      setInternalNoteBeingDeleted(undefined);
      internalNoteDeleteOpener.close();
    }
  }, [destroyInternalNote, internalNoteBeingDeleted, internalNoteDeleteOpener]);

  const handleStartInternalNoteEdit = useCallback(
    (note: InternalNoteType) => {
      setInternalNoteBeingEdited(note);
      internalNoteModalOpener.open();
    },
    [internalNoteModalOpener]
  );

  const handleStartInternalNoteDelete = useCallback(
    (note: InternalNoteType) => {
      setInternalNoteBeingDeleted(note);
      internalNoteDeleteOpener.open();
    },
    [internalNoteDeleteOpener]
  );

  const internalNoteDayGroups = useMemo(() => {
    if (journey.initialConsultReport?.internalNotes) {
      return groupByTimestampDay<InternalNoteType>(
        journey.initialConsultReport.internalNotes,
        internalNote => internalNote.createdAt,
        intl
      );
    }
    return [];
  }, [intl, journey.initialConsultReport.internalNotes]);

  const clientAttendees = journey.initialConsultReport.consult?.clientAttendees;

  const clientRole = useMemo(
    () => journey.owner.roles.find(role => role.journeyId === journey.id),
    [journey.id, journey.owner.roles]
  );

  const medicalConditions = useMemo(() => {
    const conditions =
      journey.careProfile.medicalConditions?.medicalConditionsOptions.map(option =>
        intl.formatMessage({
          id: `models.pme.questions.medical_conditions.options.${option}.content`
        })
      ) ?? [];

    if (journey.careProfile.medicalConditions?.other) {
      conditions?.push(journey.careProfile.medicalConditions.other);
    }
    return conditions;
  }, [
    journey.careProfile.medicalConditions?.medicalConditionsOptions,
    journey.careProfile.medicalConditions?.other,
    intl
  ]);

  const sanitizedExpertFollowups = useSanitizeHtml(
    journey.initialConsultReport.expertFollowups,
    journey.id,
    journey.initialConsultReport
  );

  const sanitizedClientFollowups = useSanitizeHtml(
    journey.initialConsultReport.clientFollowups,
    journey.id,
    journey.initialConsultReport
  );

  return (
    <>
      <div className={styles.backButtonSpacing}>
        <BackButton
          routerLink={journeyRoute({
            journeyId: journey.id,
            linkAfterJourney: '/care-profile',
            myExpert
          })}
        />
      </div>
      <div className="ion-hide-sm-up ion-margin-vertical">
        <TertiaryButton
          icon="chevronLeft"
          routerLink={journeyRoute({
            journeyId: journey.id,
            linkAfterJourney: `/initial-consult-report`,
            myExpert
          })}
          textKey="dictionary.menu"
        />
      </div>
      {!journey.initialConsultReport.published && (
        <IonGrid>
          <IonRow>
            <IonCol className={styles.buttonGroup} size="auto">
              <SecondaryButton
                data-test="report-edit"
                routerLink={journeyRoute({
                  journeyId: journey.id,
                  linkAfterJourney: `/initial-consult-report/edit`,
                  myExpert
                })}
                textKey="dictionary.edit"
              />

              <PrimaryButton data-test="publish" onClick={handlePublish} textKey="models.consultReport.publishReport" />
            </IonCol>
          </IonRow>
        </IonGrid>
      )}
      <div className={styles.splitPage}>
        <div
          className={
            currentSection === InitialConsultReportSection.MAIN ? styles.menu : 'ion-hide-sm-down ' + styles.menu
          }
        >
          <InitialConsultMenu currentSection={currentSection} journeyId={journey.id} />
        </div>

        <div className={styles.content}>
          <div
            className={`${styles.internalNotes}  ${
              currentSection === InitialConsultReportSection.MAIN ? 'ion-hide-sm-down' : ''
            }`}
          >
            <>
              {!journey.initialConsultReport.published && (
                <InternalNoteForm
                  initialConsultReportId={journey.initialConsultReport.id}
                  journeyId={journey.id}
                  skipCancel
                />
              )}
              {internalNoteDayGroups.map(group => (
                <div key={`${group.year}-${group.month}-${group.date}`}>
                  <div className={styles.internalNoteSpacing}>
                    <DateGroupBanner date={group.date} month={group.month} year={group.year} />
                  </div>
                  {group.models.map(internalNote => (
                    <InternalNote
                      journeyId={journey.id}
                      key={internalNote.id}
                      note={internalNote}
                      onDelete={handleStartInternalNoteDelete}
                      onEdit={handleStartInternalNoteEdit}
                    />
                  ))}
                </div>
              ))}

              <DeleteConfirmationAlert
                messageKey="models.internalNote.reallyDelete"
                onDelete={handleInternalNoteDelete}
                opener={internalNoteDeleteOpener}
              />

              <Modal
                opener={internalNoteModalOpener}
                title={intl.formatMessage({ id: 'models.internalNote.editInternalNote' })}
              >
                {() => (
                  <InternalNoteForm
                    initialConsultReportId={journey.initialConsultReport.id}
                    internalNote={internalNoteBeingEdited}
                    journeyId={journey.id}
                    onCancel={internalNoteModalOpener.close}
                    onSuccess={internalNoteModalOpener.close}
                  />
                )}
              </Modal>
            </>
          </div>
          <div
            className={`${styles.questions} ${
              currentSection === InitialConsultReportSection.MAIN ? 'ion-hide-sm-down' : ''
            }`}
          >
            <div className="ion-margin-bottom">
              <HugeText>
                <FormattedMessage id={`pages.client.careProfile.sections.${currentSection}`} />
              </HugeText>
            </div>

            <div
              className={
                currentSection === InitialConsultReportSection.CLIENT_INFO ||
                currentSection === InitialConsultReportSection.MAIN
                  ? undefined
                  : 'ion-hide'
              }
            >
              <InitialConsultQuestion
                selections={[journey.owner.fullName]}
                titleKey="models.initialConsultReport.questions.client_name.content"
              />
              <InitialConsultQuestion
                selections={[journey.owner.employer?.name ?? null]}
                titleKey="models.initialConsultReport.questions.employer.content"
              />
              <InitialConsultQuestion
                selections={[journey.owner.location.address]}
                titleKey="models.initialConsultReport.questions.client_location.content"
              />
              {journey.owner.location.countryCode && (
                <InitialConsultQuestion
                  selections={[
                    journey.owner.location.countryCode ? countryNameForCode(journey.owner.location.countryCode) : null
                  ]}
                  titleKey="models.initialConsultReport.questions.client_country_code.content"
                />
              )}
              <InitialConsultQuestion
                selections={[
                  journey.owner.isVeteran
                    ? intl.formatMessage({ id: 'dictionary.yes' })
                    : journey.owner.isVeteran === false
                    ? intl.formatMessage({ id: 'dictionary.no' })
                    : null
                ]}
                titleKey="models.initialConsultReport.questions.client_is_veteran.content"
              />
              <InitialConsultQuestion
                selections={[journey.owner.culturalConsiderations]}
                titleKey="models.initialConsultReport.questions.client_cultural_considerations.content"
              />
              <InitialConsultQuestion
                selections={
                  journey.careProfile.needsFromGrayce?.map(need =>
                    intl.formatMessage({ id: `models.careProfile.needsFromGrayce.${need}` })
                  ) ?? []
                }
                titleKey="forms.careProfile.needsFromGrayceLabel"
              />
              <InitialConsultQuestion
                selections={journey.careProfile.concerns.map(option =>
                  intl.formatMessage({
                    id: `models.focusArea.${option}`
                  })
                )}
                titleKey="forms.careProfile.concernsLabel"
              />
            </div>
            <div className={currentSection === InitialConsultReportSection.BASIC_INFO ? undefined : 'ion-hide'}>
              <InitialConsultQuestion selections={[journey.careProfile.name]} titleKey="forms.careProfile.nameLabel" />
              <InitialConsultQuestion
                selections={[
                  clientRole?.relationship?.relationshipOption === ClientRoleRelationshipEnum.other
                    ? clientRole?.relationship?.other
                    : clientRole?.relationship?.relationshipOption
                    ? intl.formatMessage({
                        id: `models.careProfile.relationship.${clientRole?.relationship?.relationshipOption}`
                      })
                    : null
                ]}
                titleKey="forms.relationship.label"
                titleValues={{
                  firstName: journey.careProfile.name
                }}
              />
              <InitialConsultQuestion
                selections={[
                  journey.careProfile.maritalStatus
                    ? intl.formatMessage({
                        id: `models.initialConsultReport.questions.marital_status.options.${journey.careProfile.maritalStatus}.content`
                      })
                    : null
                ]}
                titleKey="models.initialConsultReport.questions.marital_status.content"
              />
              <InitialConsultQuestion
                selections={[journey.careProfile.location.address]}
                titleKey="forms.careProfile.locationLabel"
              />
              <InitialConsultQuestion
                selections={[
                  journey.careProfile.location.countryCode
                    ? countryNameForCode(journey.careProfile.location.countryCode)
                    : null
                ]}
                titleKey="models.initialConsultReport.questions.care_profile_country_code.content"
              />
              <InitialConsultQuestion
                selections={[
                  journey.careProfile.languageCode
                    ? languageNameForCode(journey.careProfile.languageCode)
                    : intl.formatMessage({ id: 'dictionary.none' })
                ]}
                titleKey="models.initialConsultReport.questions.care_profile_language_code.content"
              />
              <InitialConsultQuestion
                selections={[
                  journey.careProfile.age
                    ? intl.formatMessage({ id: `models.careProfile.age.${journey.careProfile.age}` })
                    : null
                ]}
                titleKey="forms.careProfile.ageLabel"
              />
              <InitialConsultQuestion
                selections={[
                  journey.careProfile?.religion?.religionOption === CareProfileReligionEnum.other
                    ? journey.careProfile.religion?.other
                    : journey.careProfile.religion?.religionOption
                    ? intl.formatMessage({
                        id: `models.initialConsultReport.questions.religion.options.${journey.careProfile.religion.religionOption}.content`
                      })
                    : null
                ]}
                titleKey="models.initialConsultReport.questions.religion.content"
              />
              <InitialConsultQuestion
                selections={[journey.careProfile.culturalConsiderations]}
                titleKey="models.initialConsultReport.questions.cultural_considerations.content"
              />
            </div>
            <div className={currentSection === FocusAreaEnum.medical ? undefined : 'ion-hide'}>
              <InitialConsultQuestion
                selections={[
                  journey.careProfile.recentHospitalization
                    ? intl.formatMessage({ id: 'dictionary.yes' })
                    : journey.careProfile.recentHospitalization === false
                    ? intl.formatMessage({ id: 'dictionary.no' })
                    : null
                ]}
                titleKey="models.initialConsultReport.questions.recent_hospitalization.content"
              />
              <InitialConsultQuestion
                selections={[journey.careProfile.recentHospitalizationSynopsis]}
                titleKey="models.initialConsultReport.questions.recent_hospitalization.synopsis"
              />
              <InitialConsultQuestion
                selections={
                  journey.careProfile.medicationAssistanceNeeds?.map(option =>
                    intl.formatMessage({
                      id: `models.careProfile.questions.medication_assistance_needs.options.${option}.content`
                    })
                  ) ?? []
                }
                titleKey="models.careProfile.questions.medication_assistance_needs.content"
              />
              <InitialConsultQuestion
                selections={[
                  journey.careProfile.medicationsAffordable
                    ? intl.formatMessage({
                        id: `models.careProfile.questions.medications_affordable.options.${journey.careProfile.medicationsAffordable}.content`
                      })
                    : null
                ]}
                titleKey="models.careProfile.questions.medications_affordable.content"
              />
              <InitialConsultQuestion
                selections={[
                  journey.careProfile.primaryCareProviderStatus
                    ? intl.formatMessage({
                        id: `models.careProfile.questions.primary_care_provider_status.options.${journey.careProfile.primaryCareProviderStatus}.content`
                      })
                    : null
                ]}
                titleKey="models.careProfile.questions.primary_care_provider_status.content"
              />
              <InitialConsultQuestion
                selections={medicalConditions}
                titleKey="models.pme.questions.medical_conditions.content"
              />
              {journey.careProfile.medicalConditions?.medicalConditionsOptions.includes(
                CareProfileMedicalConditionEnum.dementia
              ) && (
                <InitialConsultQuestion
                  selections={[
                    journey.careProfile.dementiaSeverity
                      ? intl.formatMessage({
                          id: `models.initialConsultReport.questions.dementia_severity.options.${journey.careProfile.dementiaSeverity}.content`
                        })
                      : null
                  ]}
                  titleKey="models.initialConsultReport.questions.dementia_severity.content"
                />
              )}
            </div>
            <div className={currentSection === FocusAreaEnum.functional ? undefined : 'ion-hide'}>
              <InitialConsultQuestion
                selections={[
                  journey.careProfile.residenceType?.residenceTypeOption === CareProfileResidenceTypeEnum.other
                    ? journey.careProfile.residenceType.other
                    : journey.careProfile.residenceType?.residenceTypeOption
                    ? intl.formatMessage({
                        id: `models.pme.questions.residence_type.options.${journey.careProfile.residenceType?.residenceTypeOption}.content`
                      })
                    : null
                ]}
                titleKey="models.pme.questions.residence_type.content"
              />
              <InitialConsultQuestion
                selections={[
                  journey.careProfile.consideringHousingChange
                    ? intl.formatMessage({ id: 'dictionary.yes' })
                    : journey.careProfile.consideringHousingChange === false
                    ? intl.formatMessage({ id: 'dictionary.no' })
                    : null
                ]}
                titleKey="models.careProfile.questions.considering_housing_change.content"
              />
              <InitialConsultQuestion
                selections={
                  journey.careProfile.caregiverTypes?.caregiverTypesOptions?.map(option => {
                    if (option === CareProfileCaregiverTypeEnum.other && journey.careProfile.caregiverTypes?.other) {
                      return journey.careProfile.caregiverTypes?.other;
                    }
                    return intl.formatMessage({
                      id: `models.initialConsultReport.questions.caregiver_types.options.${option}.content`
                    });
                  }) ?? []
                }
                titleKey="models.initialConsultReport.questions.caregiver_types.content"
              />
              <InitialConsultQuestion
                selections={
                  journey.careProfile.cognitiveStatuses?.map(option =>
                    intl.formatMessage({ id: `models.safety.questions.cognitive_statuses.options.${option}.content` })
                  ) ?? []
                }
                titleKey="models.safety.questions.cognitive_statuses.content"
              />
              <InitialConsultQuestion
                selections={
                  journey.careProfile.stabilitySupports?.map(option =>
                    intl.formatMessage({ id: `models.safety.questions.stability_supports.options.${option}.content` })
                  ) ?? []
                }
                titleKey="models.safety.questions.stability_supports.content"
              />
              <InitialConsultQuestion
                selections={[
                  journey.careProfile.fallCount
                    ? intl.formatMessage({
                        id: `models.safety.questions.fall_count.options.${journey.careProfile.fallCount}.content`
                      })
                    : null
                ]}
                titleKey="models.safety.questions.fall_count.content"
              />
              <InitialConsultQuestion
                selections={[
                  journey.careProfile.stairsRequiredForExit
                    ? intl.formatMessage({ id: 'dictionary.yes' })
                    : journey.careProfile.stairsRequiredForExit === false
                    ? intl.formatMessage({ id: 'dictionary.no' })
                    : null
                ]}
                titleKey="models.safety.questions.stairs_required_for_exit.content"
              />
              <InitialConsultQuestion
                selections={
                  journey.careProfile.emergencyPlanItems?.map(option =>
                    intl.formatMessage({ id: `models.safety.questions.emergency_plan_items.options.${option}.content` })
                  ) ?? []
                }
                titleKey="models.safety.questions.emergency_plan_items.content"
              />
              <div className="ion-margin-bottom">
                <div>
                  <LargeText>
                    <FormattedMessage id="models.initialConsultReport.questions.stages.content" />
                  </LargeText>
                </div>
                <ActivityStage stage={journey.careProfile.stageBathingAndDressing} type="bathing_and_dressing" />
                <ActivityStage stage={journey.careProfile.stageContinence} type="continence" />
                <ActivityStage stage={journey.careProfile.stageEating} type="eating" />
                <ActivityStage stage={journey.careProfile.stageHousekeeping} type="housekeeping" />
                <ActivityStage stage={journey.careProfile.stageMealPrep} type="meal_prep" />
                <ActivityStage stage={journey.careProfile.stageShopping} type="shopping" />
                <ActivityStage stage={journey.careProfile.stageToileting} type="toileting" />
                <ActivityStage stage={journey.careProfile.stageTransportation} type="transportation" />
              </div>
              <InitialConsultQuestion
                selections={[
                  journey.careProfile.helpLevelRequired
                    ? intl.formatMessage({
                        id: `models.pme.questions.help_level_required.options.${journey.careProfile.helpLevelRequired}.content`
                      })
                    : null
                ]}
                titleKey="models.pme.questions.help_level_required.content"
              />
            </div>
            <div className={currentSection === FocusAreaEnum.legal ? undefined : 'ion-hide'}>
              {journey.legalDocuments.map(document => (
                <InitialConsultQuestion
                  key={document.id}
                  selections={[intl.formatMessage({ id: `models.client.legalDocument.${document.status}` })]}
                  titleKey={`pages.client.legalDocuments.titles.${document.kind}`}
                />
              ))}
            </div>

            <div className={currentSection === FocusAreaEnum.financial ? undefined : 'ion-hide'}>
              <InitialConsultQuestion
                selections={
                  journey.careProfile.healthInsuranceTypes?.healthInsuranceTypesOptions?.map(option => {
                    if (
                      option === CareProfileHealthInsuranceTypeEnum.other &&
                      journey.careProfile.healthInsuranceTypes?.other
                    ) {
                      return journey.careProfile.healthInsuranceTypes?.other;
                    }
                    return intl.formatMessage({
                      id: `models.initialConsultReport.questions.health_insurance_types.options.${option}.content`
                    });
                  }) ?? []
                }
                titleKey="models.initialConsultReport.questions.health_insurance_types.content"
              />
              <InitialConsultQuestion
                selections={[
                  journey.careProfile.vaConnected
                    ? intl.formatMessage({ id: 'dictionary.yes' })
                    : journey.careProfile.vaConnected === false
                    ? intl.formatMessage({ id: 'dictionary.no' })
                    : null
                ]}
                titleKey="models.initialConsultReport.questions.va_connected.content"
              />
              <InitialConsultQuestion
                selections={
                  journey.careProfile.veteranConnection?.map(connection =>
                    intl.formatMessage({
                      id: `models.initialConsultReport.questions.veteran_connection.options.${connection}.content`
                    })
                  ) ?? []
                }
                titleKey="models.initialConsultReport.questions.veteran_connection.content"
              />
              <InitialConsultQuestion
                selections={[
                  journey.careProfile.financialManager?.other
                    ? journey.careProfile.financialManager.other
                    : journey.careProfile.financialManager?.financialManagerOption
                    ? intl.formatMessage({
                        id: `models.initialConsultReport.questions.financial_manager.options.${journey.careProfile.financialManager.financialManagerOption}.content`
                      })
                    : null
                ]}
                titleKey="models.initialConsultReport.questions.financial_manager.content"
              />
              <InitialConsultQuestion
                selections={
                  journey.careProfile.insuranceTypes?.insuranceTypesOptions?.map(option => {
                    if (option === CareProfileInsuranceTypeEnum.other && journey.careProfile.insuranceTypes?.other) {
                      return journey.careProfile.insuranceTypes.other;
                    }
                    return intl.formatMessage({
                      id: `models.initialConsultReport.questions.insurance_types.options.${option}.content`
                    });
                  }) ?? []
                }
                titleKey="models.initialConsultReport.questions.insurance_types.content"
              />
            </div>
            <div className={currentSection === FocusAreaEnum.social ? undefined : 'ion-hide'}>
              <InitialConsultQuestion
                selections={
                  journey.careProfile.emotionalConcerns?.emotionalConcernsOptions?.map(option => {
                    if (
                      option === CareProfileEmotionalConcernEnum.other &&
                      journey.careProfile.emotionalConcerns?.other
                    ) {
                      return journey.careProfile.emotionalConcerns.other;
                    }
                    return intl.formatMessage({
                      id: `models.initialConsultReport.questions.emotional_concerns.options.${option}.content`
                    });
                  }) ?? []
                }
                titleKey="models.initialConsultReport.questions.emotional_concerns.content"
              />
            </div>

            <div className={currentSection === FocusAreaEnum.wellbeing ? undefined : 'ion-hide'}>
              <InitialConsultQuestion
                selections={[
                  clientRole?.providerEmotionalState
                    ? intl.formatMessage({
                        id: `models.pme.questions.provider_emotional_state.options.${clientRole.providerEmotionalState}.content`
                      })
                    : null
                ]}
                titleKey="models.pme.questions.provider_emotional_state.content"
              />
            </div>
            <div className={currentSection === InitialConsultReportSection.STAGE ? undefined : 'ion-hide'}>
              <StageInfo stage={journey.stage} />
            </div>
            <div className={currentSection === InitialConsultReportSection.CONCLUSION ? undefined : 'ion-hide'}>
              <div className="ion-margin-bottom">
                <div>
                  <LargeText>
                    <FormattedMessage id="models.initialConsultReport.questions.needs.content" />
                  </LargeText>
                </div>

                {/* if none are selected, focus area is met */}
                {journey.careProfile.needsMetMedications === null &&
                  journey.careProfile.needsMetMentalHealth === null &&
                  journey.careProfile.needsMetMedicalResources === null && <NeedMet met type="medical" />}

                {journey.careProfile.needsMetCare === null &&
                  journey.careProfile.needsMetFood === null &&
                  journey.careProfile.needsMetTransportation === null &&
                  journey.careProfile.needsMetEmergencyPlanning === null &&
                  journey.careProfile.needsMetBasicNeeds === null && <NeedMet met type="functional" />}

                <NeedMet met={journey.careProfile.needsMetMedicalResources} type="medicalResources" />
                <NeedMet met={journey.careProfile.needsMetMedications} type="medications" />
                <NeedMet met={journey.careProfile.needsMetMentalHealth} type="mentalHealth" />
                <NeedMet met={journey.careProfile.needsMetCare} type="care" />
                <NeedMet met={journey.careProfile.needsMetFood} type="food" />
                <NeedMet met={journey.careProfile.needsMetTransportation} type="transportation" />
                <NeedMet met={journey.careProfile.needsMetEmergencyPlanning} type="emergencyPlanning" />
                <NeedMet met={journey.careProfile.needsMetBasicNeeds} type="basicNeeds" />
                <NeedMet met={journey.careProfile.needsMetLegal} type="legal" />

                {journey.careProfile.needsMetFinancialPrograms === null &&
                  journey.careProfile.needsMetInsurance === null &&
                  journey.careProfile.needsMetFinancialPlanning === null && <NeedMet met type="financial" />}

                <NeedMet met={journey.careProfile.needsMetFinancialPrograms} type="financialPrograms" />
                <NeedMet met={journey.careProfile.needsMetInsurance} type="insurance" />
                <NeedMet met={journey.careProfile.needsMetFinancialPlanning} type="financialPlanning" />
                <NeedMet met={journey.careProfile.needsMetSocial} type="social" />
                <NeedMet met={journey.careProfile.needsMetYourWellbeing} type="yourWellbeing" />
              </div>

              <InitialConsultQuestion
                selections={[
                  journey.initialConsultReport.benefitsExplained
                    ? intl.formatMessage({ id: 'dictionary.yes' })
                    : journey.initialConsultReport.benefitsExplained === false
                    ? intl.formatMessage({ id: 'dictionary.no' })
                    : null
                ]}
                titleKey="models.initialConsultReport.questions.conclusion.benefitsExplained"
              />
              <div className="ion-margin-top">
                <LargeText>
                  <FormattedMessage id="models.initialConsultReport.questions.conclusion.expertFollowups" />
                </LargeText>
                {sanitizedExpertFollowups}
              </div>

              <div className="ion-margin-top">
                <LargeText>
                  <FormattedMessage id="models.initialConsultReport.questions.conclusion.clientFollowups" />
                </LargeText>
                {sanitizedClientFollowups}
              </div>
              {clientAttendees?.clients && (
                <div className="ion-margin-top">
                  <LargeText>
                    <FormattedMessage id="forms.consultReport.clientAttendeesLabel" />
                  </LargeText>
                  <div>
                    {clientAttendees.clients.map((client, index) => (
                      <IonText key={client.id}>
                        {client.fullName}
                        {index < clientAttendees.clients.length - 1 && (
                          <>
                            <FormattedMessage id="dictionary.punctuation.comma" />
                            <Nbsp />
                          </>
                        )}
                      </IonText>
                    ))}
                    {clientAttendees.other && (
                      <>
                        {clientAttendees.clients.length > 0 && (
                          <>
                            <FormattedMessage id="dictionary.punctuation.comma" />
                            <Nbsp />
                          </>
                        )}
                        {clientAttendees.other}
                      </>
                    )}
                  </div>
                </div>
              )}

              <div className="ion-margin-top">
                <LargeText>
                  <FormattedMessage id="models.initialConsultReport.questions.conclusion.nextConsult" />
                </LargeText>
                {journey.careProfile.nextConsult && (
                  <p>
                    <DateStamp absoluteDate value={journey.careProfile.nextConsult} />
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InitialConsultReport;
